import { Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { environment } from "src/environments/environment";
import { ApiService } from "./api.service";
import { GlobalDataService } from "./global-data.service";
import chroma from "chroma-js";
import { invertColor } from "src/app/utils/colors";
import { PostItemSEO, PostItemSocial } from "../types/post";
import { SeoService } from "./seo.service";
import { Platform } from "@ionic/angular";

@Injectable()
export class AppConfigService {
  public version: string;
  public apiEndpointSomeData: string;
  public twitterLibrary: string;
  public seo: PostItemSEO;
  public social: PostItemSocial;
  public cordova: boolean = false;

  constructor(
    private API: ApiService,
    private globalData: GlobalDataService,
    private http: HttpClient,
    private seoService: SeoService,
    private platform: Platform
  ) {
    this.platform.ready().then(() => {
      this.cordova = this.platform.is("cordova");
    });
  }

  /**
   * Load Twitter script required for embeds
   * Get & store Mall data used globally for general information
   * If movies are enabled fetch movies data & store
   * Set default SEO data
   * If custom colors are set inject these and overwrite CSS vars
   */
  load() {
    this.http
      .get("https://platform.twitter.com/widgets.js", { responseType: "text" })
      .subscribe({
        next: (resp) => {
          this.twitterLibrary = resp;
        },
      });

    return new Promise<void>((resolve, reject) => {
      this.API.GET(`public/mall/${environment.mall_id}`).subscribe({
        next: (response) => {
          this.globalData.setMallData({
            ...response,
            cinemaProvider: response.movieConfig
              ? {
                  name: response.movieConfig?.moviesProvider || null,
                  cineplexId: response.movieConfig?.cineplexId || null,
                  moviesEnabled: response.movieConfig.moviesEnabled || false,
                  slug:
                    response.movieConfig?.moviesProvider &&
                    response.movieConfig?.moviesProvider.length > 0
                      ? response.movieConfig?.moviesProvider
                          .replace("-", "")
                          .replace(" ", "")
                          .toLowerCase()
                      : null,
                  listingUrl:
                    response.movieConfig?.moviesProvider === "Nu Metro"
                      ? "https://numetro.co.za/now-showing/"
                      : "https://www.sterkinekor.com/",
                  comingSoonUrl:
                    response.movieConfig?.moviesProvider === "Nu Metro"
                      ? "https://numetro.co.za/coming-soon/"
                      : "https://www.sterkinekor.com/",
                }
              : null,
          });

          resolve();

          // SEO
          this.seo = {
            title: response.general.seoTitle,
            description: response.general.seoDescription,
            keywords: response.general.metaKeywords,
          };

          if (response.social?.openGraphTitle) {
            this.social = {
              title: response.social.openGraphTitle,
              description: response.social.openGraphDescription,
              socialImage: response.social.openGraphImage,
            };
          }

          if (!this.cordova) {
            // Sitemap URL
            this.seoService.setSitemap(
              response.general.websiteURL +
                "/sitemap/" +
                environment.mall_id +
                ".xml"
            );

            // Google Verification
            this.seoService.setGoogleVerification(
              environment.google_verification
            );

            // Title tag
            this.seoService.setTitle(this.seo.title);

            // Meta tags
            this.seoService.setMeta(this.seo);

            // Favicon
            this.seoService.setFavicon(
              `/assets/favicon-${environment.environment_name}.jpeg`
            );

            if (this.social) this.seoService.setSEOMeta(this.social);

            if (response.social?.facebookAppId)
              this.seoService.setFBAppID(response.social.facebookAppId);

            if (response?.general?.googleTagManagerTag) {
              (function (w, d, s, l, i) {
                w[l] = w[l] || [];
                w[l].push({
                  "gtm.start": new Date().getTime(),
                  event: "gtm.js",
                });
                var f = d.getElementsByTagName(s)[0],
                  j = <HTMLScriptElement>d.createElement(s),
                  dl = l != "dataLayer" ? "&l=" + l : "";
                j.async = true;
                j.src = "https://www.googletagmanager.com/gtm.js?id=" + i + dl;
                f.parentNode.insertBefore(j, f);
              })(
                window,
                document,
                "script",
                "dataLayer",
                response.general.googleTagManagerTag
              );
            }
          }

          // Themeing
          if (
            response.theming.themePrimaryColor &&
            response.theming.themeSecondaryColor &&
            response.theming.themeTertiaryColor
          ) {
            const styles = [
              {
                name: "--ion-color-primary",
                value: response.theming.themePrimaryColor,
              },
              {
                name: "--ion-color-secondary",
                value: response.theming.themeSecondaryColor,
              },
              {
                name: "--ion-color-tertiary",
                value: response.theming.themeTertiaryColor,
              },
              {
                name: "--ion-color-success",
                value: response.theming.themeSuccess,
              },
              {
                name: "--ion-color-warning",
                value: response.theming.themeWarning,
              },
              {
                name: "--ion-color-danger",
                value: response.theming.themeDanger,
              },
              {
                name: "--ion-color-dark",
                value: response.theming.themeDark,
              },
              {
                name: "--ion-color-medium",
                value: response.theming.themeMedium,
              },
              {
                name: "--ion-color-light",
                value: response.theming.themeLight,
              },
            ];

            if (typeof document !== "undefined") {
              styles.forEach((data) => {
                document.documentElement.style.setProperty(
                  `${data.name}`,
                  data.value
                );
                document.documentElement.style.setProperty(
                  `${data.name}-rgb`,
                  chroma(data.value).rgb()
                );
                document.documentElement.style.setProperty(
                  `${data.name}-contrast`,
                  invertColor(data.value, true)
                );
                document.documentElement.style.setProperty(
                  `${data.name}-contrast-rgb`,
                  chroma(invertColor(data.value, true)).rgb()
                );
                document.documentElement.style.setProperty(
                  `${data.name}-shade`,
                  chroma(data.value).darken()
                );
                document.documentElement.style.setProperty(
                  `${data.name}-tint`,
                  chroma(data.value).brighten()
                );
              });

              document.documentElement.style.setProperty(
                "--swix-color-footer",
                chroma(
                  invertColor(response.theming.themePrimaryColor, true)
                ).darken(3)
              );
            }
          }
        },
        error: () => {},
      });
    });
  }

  /**
   * Add library as inline JS script
   *
   * @param {string} jsAsString
   * @memberof AppConfigService
   */
  executeLibrary(jsAsString: string) {
    if (typeof document !== "undefined") {
      const script = document.createElement("script");
      script.type = "text/javascript";
      script.innerHTML = jsAsString;
      document.getElementsByTagName("head")[0].appendChild(script);
    }
  }
}
